import { MdOutlineCloud } from 'react-icons/md';
import { Drawer, DrawerAction, DrawerBody, DrawerHeader } from '@/components/Drawer';
import { useStyletron } from '@tigergraph/app-ui-lib/Theme';
import { Button } from '@tigergraph/app-ui-lib/button';
import { SubmitHandler, useForm } from 'react-hook-form';
import shortUUID from 'short-uuid';
import { useMutation, useQueryClient } from 'react-query';
import { Result } from '@/lib/type';
import {
  CreateCloudProviderRequest as CreateProviderRequest,
  createProvider,
} from '@/pages/admin/settings/cloud_provider/api';
import { useOrgContext } from '@/contexts/orgContext';
import { showToast } from '@/components/styledToasterContainer';
import { getErrorMessage } from '@/utils/utils';
import { useEffect, useState } from 'react';
import { useCanManageCloudProviders } from '@/pages/admin/settings/cloud_provider/hook';
import { NO_CP_MANAGE_PERMISSION_MESSAGE } from '@/pages/admin/settings/cloud_provider/util';
import Breadcrumbs from '@/components/Breadcrumbs';
import SelectVendor from '@/pages/admin/settings/cloud_provider/SelectVendor';
import CreateRole from '@/pages/admin/settings/cloud_provider/CreateRole';
import { IFormInput } from '@/pages/admin/settings/cloud_provider/types';
import { CloudPlatform } from '@/pages/admin/settings/cloud_provider/CloudProviderCategory';
import { KIND } from 'baseui/button';
import StyledToast from '@tigergraph/app-ui-lib/styledToasterContainer/styledToast';
import { Spinner } from '@tigergraph/app-ui-lib/spinner';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

enum Steps {
  CONFIGURE_VENDOR = 0,
  CREATE_ROLE = 1,
}

export default function AddCloudProvider({ isOpen, onClose }: Props) {
  const [css, theme] = useStyletron();
  const queryClient = useQueryClient();

  const { currentOrg } = useOrgContext();
  const canManageCloudProviders = useCanManageCloudProviders();

  const [stepIndex, setStepIndex] = useState(Steps.CONFIGURE_VENDOR);
  const [submitted, setSubmitted] = useState(false);

  const createProviderMutation = useMutation<Result<void>, Error, CreateProviderRequest>(
    async (data) => {
      const res = await createProvider(data);
      return res;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['cloud-providers']);
      },
    }
  );

  const {
    handleSubmit,
    formState: { errors },
    register,
    control,
    watch,
    setValue,
    trigger: validateForm,
    setFocus,
    reset,
  } = useForm<IFormInput>({
    defaultValues: {
      name: '',
      region: '',
      awsRoleArn: '',
      awsExternalId: shortUUID().generate(),
      trustedAccountId: '',
      secureConnection: false,
    },
  });

  const name = watch('name');
  const awsRoleArn = watch('awsRoleArn');

  const [cloudPlatform, setCloudPlatform] = useState<CloudPlatform>(CloudPlatform.AWS);

  const onSubmit: SubmitHandler<IFormInput> = (data) => {
    if (!canManageCloudProviders) {
      showToast({
        kind: 'negative',
        message: NO_CP_MANAGE_PERMISSION_MESSAGE,
      });
      return;
    }

    createProviderMutation.mutate(
      {
        org_id: currentOrg.org_id,
        platform: cloudPlatform,
        region: data.region,
        name: data.name,
        role_arn: data.awsRoleArn,
        role_external_id: data.awsExternalId,
        type: '',
        options: {
          secure_connection: data.secureConnection,
          account_id: data.trustedAccountId,
        },
      },
      {
        onSuccess: () => {
          setSubmitted(true);
          queryClient.invalidateQueries(['cloud-providers']);
        },
        onError: (error) => {
          showToast({
            kind: 'negative',
            message: getErrorMessage(error),
          });
        },
      }
    );
  };

  useEffect(() => {
    if (isOpen) {
      setStepIndex(Steps.CONFIGURE_VENDOR);
      setSubmitted(false);
      setCloudPlatform(CloudPlatform.AWS);
      reset({
        name: '',
        region: '',
        awsRoleArn: '',
        awsExternalId: shortUUID().generate(),
        trustedAccountId: '',
        secureConnection: false,
      });
    }
  }, [isOpen, reset]);

  return (
    <Drawer isOpen={isOpen} onClose={onClose} size="auto">
      <DrawerHeader hideLine>
        <div className={css({ display: 'flex', alignItems: 'center' })}>
          <MdOutlineCloud size={24} /> <span className={css({ marginLeft: '8px' })}>Add Cloud Provider</span>
        </div>
      </DrawerHeader>
      <Breadcrumbs names={['Configure Vendor', 'Create Role']} index={stepIndex} compact={true} />
      <DrawerBody>
        <form>
          {stepIndex === Steps.CONFIGURE_VENDOR && (
            <SelectVendor
              control={control}
              errors={errors}
              cloudPlatform={cloudPlatform}
              onSelectPlatform={setCloudPlatform}
              setValue={setValue}
              watch={watch}
            />
          )}
          {stepIndex === Steps.CREATE_ROLE && (
            <CreateRole
              control={control}
              errors={errors}
              watch={watch}
              setFocus={setFocus}
              setValue={setValue}
              validateForm={validateForm}
              cloudPlatform={cloudPlatform}
            />
          )}
        </form>
        {stepIndex === Steps.CREATE_ROLE && !submitted && awsRoleArn && (
          <StyledToast message="Your cloud provider setup will begin after you click Add" closeable={false} />
        )}
        {submitted && (
          <StyledToast
            title="Cloud provider setup in progress"
            message="Your cloud provider is being set up. This process can take up to 30 minutes. You may leave this page while provisioning is in progress and return later to check the status."
            icon={
              <Spinner
                $size="16px"
                $borderWidth="2px"
                className={css({
                  marginRight: '8px',
                  borderTopColor: theme.colors.secondary900,
                  borderLeftColor: theme.colors.secondary900,
                  borderBottomColor: 'transparent',
                  borderRightColor: 'transparent',
                })}
              />
            }
            closeable={false}
          />
        )}
      </DrawerBody>
      <DrawerAction>
        <div className={css({ display: 'flex', gap: '16px' })}>
          {submitted ? (
            <Button kind={KIND.secondary} onClick={() => onClose()}>
              Close
            </Button>
          ) : (
            <>
              {stepIndex === Steps.CONFIGURE_VENDOR ? (
                <Button kind={KIND.secondary} onClick={() => onClose()}>
                  Cancel
                </Button>
              ) : (
                <Button kind={KIND.secondary} onClick={() => setStepIndex((prev) => prev - 1)}>
                  Back
                </Button>
              )}
              {stepIndex === Steps.CREATE_ROLE ? (
                <Button
                  isLoading={createProviderMutation.isLoading}
                  disabled={!canManageCloudProviders}
                  onClick={handleSubmit(onSubmit)}
                >
                  Add
                </Button>
              ) : (
                <Button disabled={!name} onClick={() => setStepIndex((prev) => prev + 1)}>
                  Next
                </Button>
              )}
            </>
          )}
        </div>
      </DrawerAction>
    </Drawer>
  );
}
